import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Layout from "@/components/Layout";
import { useLightbox } from "@/hooks/useLightbox";
import { Lightbox } from "@/components/ImageLightbox";

interface ProductPageProps {
  product: {
    id: number;
    name: string;
    path: string;
    image: string;
    description: string;
    specifications: string[];
    applications: string[];
    features: string[];
    physicalfeatures: string[];
  };
}

export default function ProductPage({ product }: ProductPageProps) {
  const { isOpen, src, alt, openLightbox, closeLightbox } = useLightbox();

  return (
    <Layout>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-primary/80 text-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            {product.name}
          </h1>
          <p className="text-xl text-white/90 max-w-2xl">
            Premium grade {product.name.toLowerCase()} for industrial applications
          </p>
        </div>
      </section>

      {/* Product Details */}
      <section className="py-20 md:py-28 bg-gradient-to-b from-white to-muted/30">
        <div className="container mx-auto px-4">
          {/* Product Overview Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-24">
            {/* ✅ Fixed Image Block */}
            <div
              className="relative bg-white flex items-center justify-center rounded-2xl overflow-hidden shadow-lg cursor-pointer group"
              onClick={() => openLightbox(product.image, product.name)}
            >
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-[28rem] object-contain transition-transform duration-300 group-hover:scale-105 bg-white"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = "/placeholder.svg";
                }}
              />
            </div>

            {/* Text Section */}
            <div>
              <h2 className="text-4xl font-bold text-primary mb-6 border-l-4 border-accent pl-4">
                Product Overview
              </h2>
              <p className="text-lg text-foreground/80 mb-8 leading-relaxed">
                {product.description}
              </p>

              <h3 className="text-2xl font-semibold text-primary mb-4">
                Key Features
              </h3>
              <ul className="space-y-3 mb-10">
                {product.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <span className="text-accent font-bold text-lg">✓</span>
                    <span className="text-foreground/80">{feature}</span>
                  </li>
                ))}
              </ul>

              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-accent text-accent-foreground px-8 py-3 rounded-lg font-semibold hover:opacity-90 transition-all"
              >
                Request Quote <ArrowRight size={20} />
              </Link>
            </div>
          </div>

          {/* Lightbox */}
          <Lightbox src={src} alt={alt} isOpen={isOpen} onClose={closeLightbox} />

          {/* Specifications */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 my-14">
            {/* Technical Specifications */}
            <div className="relative rounded-2xl overflow-hidden border border-border/20 bg-gradient-to-br from-white via-gray-50 to-blue-50 shadow-[0_2px_12px_rgba(0,0,0,0.05)] hover:shadow-[0_4px_18px_rgba(0,0,0,0.07)] transition-all duration-300 p-5">
              <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-primary via-blue-600 to-primary opacity-80"></div>

              <h3 className="text-xl md:text-2xl font-bold text-primary mb-4 flex items-center gap-2">
                <span className="inline-block w-1 h-5 bg-primary rounded-full"></span>
                Technical Specifications
              </h3>

              <div className="divide-y divide-border/20">
                {product.specifications.map((spec, idx) => {
                  const [key, value] = spec.split(":");
                  return (
                    <div
                      key={idx}
                      className="flex justify-between items-center py-2.5 text-sm md:text-base"
                    >
                      <span className="text-foreground/70">{key}</span>
                      <span className="font-semibold text-primary">
                        {value?.trim() || "✓"}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Packaging & Delivery */}
            <div className="relative rounded-2xl overflow-hidden border border-border/20 bg-gradient-to-br from-white via-gray-50 to-blue-50 shadow-[0_2px_12px_rgba(0,0,0,0.05)] hover:shadow-[0_4px_18px_rgba(0,0,0,0.07)] transition-all duration-300 p-5">
              <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-primary via-blue-600 to-primary opacity-80"></div>

              <h3 className="text-xl md:text-2xl font-bold text-primary mb-4 flex items-center gap-2">
                <span className="inline-block w-1 h-5 bg-primary rounded-full"></span>
                Packaging & Delivery
              </h3>

              <div className="grid gap-3">
                {[
                  {
                    title: "Standard Packaging",
                    desc: "25 kg bags, 50 kg bags, 1000/1250/1400 kg jumbo bags",
                  },
                  {
                    title: "Minimum Order Quantity",
                    desc: "28 MT per order",
                  },
                  {
                    title: "Delivery Time",
                    desc: "7–14 days from order confirmation",
                  },
                  {
                    title: "Shelf Life",
                    desc: "24 months in dry storage conditions",
                  },
                ].map((item, idx) => (
                  <div
                    key={idx}
                    className="rounded-xl bg-white border border-border/30 shadow-[0_2px_6px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)] hover:-translate-y-[2px] transition-all duration-300 p-3.5"
                  >
                    <h4 className="font-semibold text-primary mb-0.5 text-sm md:text-base">
                      {item.title}
                    </h4>
                    <p className="text-sm md:text-base text-foreground/70">
                      {item.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Applications & Industries */}
          <div className="relative bg-gradient-to-br from-[#f9fbff] via-[#f2f6ff] to-[#e7edff] rounded-2xl p-10 md:p-14 mb-20 shadow-[0_8px_24px_rgba(0,0,0,0.04)]">
            {/* Decorative glow */}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(255,255,255,0.8)_0%,transparent_70%)] pointer-events-none rounded-2xl"></div>

            {/* Full Width Applications */}
            <div className="relative z-10">
              <div className="bg-white/80 backdrop-blur-xl border border-white/60 rounded-2xl p-8 shadow-[0_4px_16px_rgba(37,99,235,0.08)] hover:shadow-[0_6px_24px_rgba(37,99,235,0.12)] transition-all duration-300 w-full">
                <div className="flex items-center gap-2 mb-5 border-b border-blue-100 pb-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m-6-8h6m2 12H7a2 2 0 01-2-2V6a2 2 0 012-2h7l5 5v11a2 2 0 01-2 2z" />
                  </svg>
                  <h4 className="text-lg md:text-xl font-bold text-blue-800">
                    Primary Applications
                  </h4>
                </div>

                <ul className="space-y-2 text-gray-700 leading-relaxed">
                  {product.applications.map((app, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-blue-500 mt-1">✓</span>
                      <span>{app}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>


          {/* Related Products */}
          <div className="relative text-center mb-20">
            {/* Decorative gradient glow */}
            <div className="absolute inset-0 bg-gradient-to-r from-blue-50 via-transparent to-blue-50 rounded-2xl blur-xl opacity-70 -z-10"></div>

            <h3 className="text-3xl font-bold mb-4">
              <span className="bg-gradient-to-r from-[#1e3a8a] to-[#2563eb] bg-clip-text text-transparent">
                Related Products
              </span>
            </h3>

            <div className="w-20 h-[3px] bg-gradient-to-r from-[#1e3a8a] to-[#2563eb] mx-auto rounded-full mb-6"></div>

            <p className="text-foreground/70 max-w-2xl mx-auto mb-8">
              Explore more from our product range that complements your requirements.
            </p>

            <Link
              to="/products"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-[#1e3a8a] to-[#2563eb] text-white px-8 py-3 rounded-lg font-semibold shadow-md hover:shadow-lg hover:opacity-95 transition-all"
            >
              View All Products <ArrowRight size={20} />
            </Link>
          </div>

          {/* CTA */}
          <div className="text-center p-12 bg-gradient-to-r from-primary to-accent text-white rounded-2xl shadow-xl">
            <h3 className="text-3xl font-bold mb-4">
              Interested in Our {product.name}?
            </h3>
            <p className="text-white/90 mb-8 max-w-2xl mx-auto">
              Contact our sales team for pricing, bulk orders, or custom specifications.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-accent text-accent-foreground px-8 py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity"
              >
                Get Quote <ArrowRight size={20} />
              </Link>
              <Link
                to="/products"
                className="inline-flex items-center gap-2 border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/10 transition-colors"
              >
                View All Products <ArrowRight size={20} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
